import { Grid, Typography } from "@mui/material";
import moment from "moment";
import { useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import CustomButton from "../../../components/CustomButton";
import CustomModal from "../../../components/CustomModal/CustomModal";
import CustomTextField from "../../../components/CustomTextField";
import CustomUpload from "../../../components/CustomUpload";
import CustomSelect from "../../../components/Select/Select";
import { showSnackBar } from "../../../redux/reducers/snackSlice";
import {
  createSupportTicket,
  getSupportTickets,
} from "../../../redux/reducers/supportSlice";
import FilesWithCloseButton from "../../Hospital/ClinicPetDetails/PatientWorkbook/FilesWithCloseButton";
import "./Support.css";
import {
  SUPPORT_CATEGORIES,
  SUPPORT_PROBLEM_TYPES,
  SUPPORT_REASONS_BY_CATEGORY,
  toSelectList,
} from "./supportData";

const initForm = {
  category: "",
  reason: "",
  problemType: "",
  description: "",
  attachments: [],
};

const initErrors = {
  category: "",
  reason: "",
  problemType: "",
  description: "",
  file: "",
};

const toFilesArray = (value) => {
  if (value == null || value === "") return [];
  if (Array.isArray(value)) return value;
  if (typeof value === "string") {
    try {
      const parsed = JSON.parse(value);
      if (Array.isArray(parsed)) return parsed;
      if (parsed) return [parsed];
    } catch {
      return [value];
    }
  }
  return [value];
};

const mapFileItem = (item, fallbackName = "file") => {
  if (typeof item === "string") {
    return { previewUrl: item, fileName: fallbackName };
  }
  return {
    previewUrl:
      item?.previewUrl ||
      item?.url ||
      item?.fileUrl ||
      item?.filePath ||
      item?.path ||
      "",
    fileName:
      item?.fileName || item?.name || item?.originalName || fallbackName,
    file: item?.file,
  };
};

const normalizeFileList = (value, fallbackName = "file") =>
  toFilesArray(value)
    .map((item) => mapFileItem(item, fallbackName))
    .filter((f) => f.previewUrl);

const normalizeTicket = (ticket, index = 0) => {
  // Attachment and files are separate arrays from API
  const attachments = normalizeFileList(
    ticket?.attachment ?? ticket?.attachments,
    "attachment",
  );
  const files = normalizeFileList(ticket?.files, "file");

  return {
    id: ticket?.id || ticket?.ticketId || `ticket_${index}`,
    ticketId: ticket?.ticketId || ticket?.id || `ticket_${index}`,
    ticketName: ticket?.ticketName || ticket?.name || "",
    ticketNumber: ticket?.ticketNumber || "",
    category: ticket?.mainCategory || ticket?.category || "",
    mainCategory: ticket?.mainCategory || ticket?.category || "",
    reason: ticket?.reason || "",
    problemType: ticket?.problem || ticket?.problemType || "",
    description: ticket?.description || "",
    remark: ticket?.remark || ticket?.remarks || "",
    attachments,
    files,
    status: ticket?.status || "Open",
    createdAt:
      ticket?.createdAt || ticket?.createdDate || ticket?.createdOn || null,
  };
};

const SupportTicketViewModal = ({ open, ticket, onClose }) => {
  if (!ticket) return null;

  const statusLabel = String(ticket?.status ?? "Open");
  const isResolved = statusLabel.toLowerCase() === "resolved";
  const attachments = Array.isArray(ticket?.attachments)
    ? ticket.attachments
    : [];
  const files = Array.isArray(ticket?.files) ? ticket.files : [];

  return (
    <CustomModal
      open={open}
      onClose={onClose}
      header={`Support Ticket - ${ticket?.ticketNumber??''}`}
      modalWidth="sm"
    >
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <Typography className="support-field-label">TICKET NAME</Typography>
          <CustomTextField
            fullWidth
            value={ticket?.ticketName || "-"}
            disabled
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <Typography className="support-field-label">STATUS</Typography>
          <CustomTextField fullWidth value={statusLabel} disabled />
        </Grid>
        <Grid item xs={12} md={6}>
          <Typography className="support-field-label">CATEGORY</Typography>
          <CustomTextField
            fullWidth
            value={ticket?.mainCategory || ticket?.category || "-"}
            disabled
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <Typography className="support-field-label">REASON</Typography>
          <CustomTextField fullWidth value={ticket?.reason || "-"} disabled />
        </Grid>
        <Grid item xs={12} md={6}>
          <Typography className="support-field-label">PROBLEM TYPE</Typography>
          <CustomTextField
            fullWidth
            value={ticket?.problemType || "-"}
            disabled
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <Typography className="support-field-label">CREATED ON</Typography>
          <CustomTextField
            fullWidth
            value={
              ticket?.createdAt
                ? moment(ticket.createdAt).format("DD MMM YYYY, hh:mm A")
                : "-"
            }
            disabled
          />
        </Grid>
        <Grid item xs={12}>
          <Typography className="support-field-label">DESCRIPTION</Typography>
          <CustomTextField
            fullWidth
            multiline
            rows={4}
            value={ticket?.description || "-"}
            disabled
          />
        </Grid>
        {attachments.length > 0 ? (
          <Grid item xs={12}>
            <Typography className="support-field-label">ATTACHMENT</Typography>
            <FilesWithCloseButton files={attachments} disabled />
          </Grid>
        ) : null}
        {isResolved ? (
          <>
            <Grid item xs={12}>
              <Typography className="support-field-label">REMARK</Typography>
              <CustomTextField
                fullWidth
                multiline
                rows={3}
                value={ticket?.remark || "-"}
                disabled
              />
            </Grid>
            {files.length > 0 ? (
              <Grid item xs={12}>
                <Typography className="support-field-label">FILES</Typography>
                <FilesWithCloseButton files={files} disabled />
              </Grid>
            ) : null}
          </>
        ) : null}
      </Grid>
    </CustomModal>
  );
};

const SupportTicketCard = ({ ticket, onView }) => {
  const statusLabel = String(ticket?.status ?? "Open");
  const isOpen = statusLabel.toLowerCase() === "open";

  return (
    <div className="support-card">
      <div className="support-card-main">
        <div className="support-card-title-row">
          <div className="support-card-title">
            {ticket?.mainCategory || ticket?.category || "-"}
          </div>
          {ticket?.ticketNumber ? (
            <div className="support-card-ticket-no">{ticket.ticketNumber}</div>
          ) : null}
          <span
            className={`support-status-pill ${
              isOpen ? "support-status-active" : "support-status-resolved"
            }`}
          >
            {isOpen ? "OPEN" : statusLabel.toUpperCase()}
          </span>
        </div>
        <div className="support-card-remarks support-card-remark-text">
          {ticket?.description || "-"}
        </div>
        <div className="support-card-meta">
          {ticket.createdAt
            ? moment(ticket.createdAt).format("DD MMM YYYY, hh:mm A")
            : "-"}
        </div>
      </div>
      <div className="support-card-actions">
        <CustomButton text="View" smallBtn onClick={() => onView(ticket)} />
      </div>
    </div>
  );
};

const Support = () => {
  const dispatch = useDispatch();
  const [view, setView] = useState("list");
  const [tickets, setTickets] = useState([]);
  const [form, setForm] = useState(initForm);
  const [errors, setErrors] = useState(initErrors);
  const [viewTicket, setViewTicket] = useState(null);

  const fetchTickets = () => {
    dispatch(getSupportTickets()).then((res) => {
      const payload = res?.payload;
      if (!payload) {
        setTickets([]);
        return;
      }
      const list = Array.isArray(payload)
        ? payload
        : Array.isArray(payload?.tickets)
          ? payload.tickets
          : Array.isArray(payload?.content)
            ? payload.content
            : [];
      setTickets(list.map((ticket, index) => normalizeTicket(ticket, index)));
    });
  };

  useEffect(() => {
    fetchTickets();
  }, []);

  const activeTickets = useMemo(
    () =>
      (tickets ?? []).filter(
        (t) => (t?.status ?? "")?.toLowerCase() === "open",
      ),
    [tickets],
  );

  const resolvedTickets = useMemo(
    () =>
      (tickets ?? []).filter(
        (t) => (t?.status ?? "")?.toLowerCase() === "resolved",
      ),
    [tickets],
  );

  const reasonOptions = useMemo(
    () => toSelectList(SUPPORT_REASONS_BY_CATEGORY[form.category] ?? []),
    [form.category],
  );

  const onChangeForm = (name, value) => {
    setForm((prev) => {
      const next = { ...prev, [name]: value };
      if (name === "category") {
        next.reason = "";
      }
      return next;
    });
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const resetForm = () => {
    setForm(initForm);
    setErrors(initErrors);
  };

  const handleRemoveAttachment = (index) => {
    setForm((prev) => ({
      ...prev,
      attachments: prev.attachments.filter((_, i) => i !== index),
    }));
    setErrors((prev) => ({ ...prev, file: "" }));
  };

  const onUploadAttachment = (e) => {
    const fileList = e?.target?.files;
    if (!fileList?.length) return;

    const selectedFiles = Array.from(fileList);
    const invalidFile = selectedFiles.find(
      (file) => !file.type?.startsWith("image/"),
    );
    if (invalidFile) {
      setErrors((prev) => ({
        ...prev,
        file: "Only image files are allowed",
      }));
      dispatch(
        showSnackBar({
          message: "Only image files are allowed",
          type: "error",
        }),
      );
      e.target.value = "";
      return;
    }

    const readFiles = selectedFiles.map(
      (file) =>
        new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onloadend = () => {
            resolve({
              file,
              previewUrl: reader.result,
              fileName: file.name,
            });
          };
          reader.onerror = () => reject(reader.error);
          reader.readAsDataURL(file);
        }),
    );

    Promise.all(readFiles)
      .then((newAttachments) => {
        setForm((prev) => ({
          ...prev,
          attachments: [...(prev.attachments ?? []), ...newAttachments],
        }));
        setErrors((prev) => ({ ...prev, file: "" }));
      })
      .catch(() => {
        dispatch(
          showSnackBar({
            message: "Unable to read selected images",
            type: "error",
          }),
        );
      })
      .finally(() => {
        e.target.value = "";
      });
  };

  const validate = () => {
    const next = { ...initErrors };
    if (!form?.category) next.category = "This field is required";
    if (!form?.reason) next.reason = "This field is required";
    if (!form?.problemType) next.problemType = "This field is required";
    if (!form?.description?.trim()) {
      next.description = "This field is required";
    }
    setErrors(next);
    return (
      !next.category && !next.reason && !next.problemType && !next.description
    );
  };

  const handleCreate = async () => {
    if (!validate()) return;

    const formData = new FormData();
    formData.append("description", form.description.trim());
    formData.append("mainCategory", form.category);
    formData.append("reason", form.reason);
    formData.append("problem", form.problemType);
    formData.append(
      "ticketName",
      `Ticket_${moment().format("YYYYMMDDHHmmss")}`,
    );

    (form.attachments ?? []).forEach((item) => {
      if (item?.file) {
        formData.append("attachment", item.file);
      }
    });

    const res = await dispatch(createSupportTicket(formData));
    if (res?.payload) {
      resetForm();
      setView("list");
      fetchTickets();
    }
  };

  const renderTicketSection = (label, list, emptyText) => (
    <div className="support-section">
      <div className="support-list-label">{label}</div>
      {list.length === 0 ? (
        <div className="support-empty">{emptyText}</div>
      ) : (
        list.map((ticket) => (
          <SupportTicketCard
            key={ticket?.ticketId || ticket?.id}
            ticket={ticket}
            onView={setViewTicket}
          />
        ))
      )}
    </div>
  );

  return (
    <div className="support-page com-mar mt10">
      <SupportTicketViewModal
        open={!!viewTicket}
        ticket={viewTicket}
        onClose={() => setViewTicket(null)}
      />
      {view === "list" ? (
        <>
          <div className="support-page-header">
            <Typography className="font-bold fs22 black">Support</Typography>
            <div>
              <CustomButton
                text="+ Create Support"
                smallBtn
                onClick={() => setView("create")}
              />
            </div>
          </div>

          <div className="support-page-card scroll-80vh">
            {renderTicketSection(
              "ACTIVE",
              activeTickets,
              "No active support tickets",
            )}
            {renderTicketSection(
              "RESOLVED",
              resolvedTickets,
              "No resolved support tickets",
            )}
          </div>
        </>
      ) : (
        <>
          <div className="support-page-header">
            <Typography className="font-bold fs22 black">
              Create Support
            </Typography>
            <div className="flex-row">
              <div className="mr10">
                <CustomButton
                  text="Cancel"
                  smallBtn
                  onClick={() => {
                    setView("list");
                    resetForm();
                  }}
                  redBtn
                />
              </div>
              <CustomButton text="Submit" smallBtn onClick={handleCreate} />
            </div>
          </div>

          <div className="support-page-card scroll-80vh">
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Typography className="support-field-label">
                  CATEGORY
                </Typography>
                <CustomSelect
                  select
                  list={toSelectList(SUPPORT_CATEGORIES)}
                  value={form.category}
                  handleChange={(e) =>
                    onChangeForm("category", e?.target?.value)
                  }
                  error={!!errors.category}
                  helperText={errors.category}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography className="support-field-label">REASON</Typography>
                <CustomSelect
                  select
                  list={reasonOptions}
                  value={form.reason}
                  handleChange={(e) => onChangeForm("reason", e?.target?.value)}
                  disabled={!form.category}
                  error={!!errors.reason}
                  helperText={errors.reason}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography className="support-field-label">
                  PROBLEM TYPE
                </Typography>
                <CustomSelect
                  select
                  list={toSelectList(SUPPORT_PROBLEM_TYPES)}
                  value={form.problemType}
                  handleChange={(e) =>
                    onChangeForm("problemType", e?.target?.value)
                  }
                  disabled={!form.reason}
                  error={!!errors.problemType}
                  helperText={errors.problemType}
                />
              </Grid>
              <Grid item xs={12}>
                <Typography className="support-field-label">
                  DESCRIPTION
                </Typography>
                <CustomTextField
                  fullWidth
                  multiline
                  rows={5}
                  placeholder="Description"
                  value={form.description}
                  handleChange={(e) =>
                    onChangeForm("description", e?.target?.value)
                  }
                  error={!!errors.description}
                  helperText={errors.description}
                />
              </Grid>
              <Grid item xs={12}>
                <Typography className="support-field-label">
                  ATTACHMENT (IMAGES ONLY)
                </Typography>
                {form.attachments?.length > 0 ? (
                  <FilesWithCloseButton
                    files={form.attachments}
                    onClickFile={handleRemoveAttachment}
                  />
                ) : null}
                <CustomUpload
                  onUploadFile={onUploadAttachment}
                  multipleNew
                  multiFiles
                  fileAccept="image/*"
                />
                {errors.file ? (
                  <Typography className="fs12 red-color mt5">
                    {errors.file}
                  </Typography>
                ) : null}
              </Grid>
            </Grid>
          </div>
        </>
      )}
    </div>
  );
};

export default Support;
